import React, { useEffect, useMemo } from 'react'

import { DispensaryAuth0Provider } from '../../context/DispensaryAuth0Provider'
import useLogAndCaptureError from '../../hooks/useLogAndCaptureError'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import {
  DispensaryContext,
  getDispensaryFromData,
  useDispensary,
  useInvalidUrlDispensary,
} from '../../lib/dispensaries'
import { setDispensary } from '../../lib/dispensaries/state'
import { ErrorPage } from '../ErrorPage'
import { LoadingPage, LoadingPageProps } from '../LoadingPage'
import { InvalidDispensaryModal } from './InvalidDispensaryModal'

export interface DispensaryContextProviderProps {
  children: React.ReactNode
  loadingPageComponent?: React.ComponentType<LoadingPageProps>
  slug: string
}

export const DispensaryContextProvider: React.FC<DispensaryContextProviderProps> = ({
  children,
  loadingPageComponent = LoadingPage,
  slug,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  // resolve slug from uri prop if not available (e.g. default route does not receive slug)
  const { loading, data, error, called } = useDispensary({ slug })
  const { loading: loadingInvalidUrlDispensary, invalidUrlDispensaryForSlug } =
    useInvalidUrlDispensary()

  const loadingPageElement = useMemo(() => {
    return React.createElement(loadingPageComponent, { title: 'Virtual Dispensary' })
  }, [loadingPageComponent])

  useLogAndCaptureError(error)

  const { disableInvalidDispensaries } = featureFlags ?? {}

  // reduce useMemo dependency list by determining whether we should call
  // `invalidUrlDispensaryForSlug` if `data` not available
  const invalidUrlDispensaryFactory =
    called && !loading && !disableInvalidDispensaries && !loadingInvalidUrlDispensary
      ? invalidUrlDispensaryForSlug
      : undefined

  const dispensary = useMemo(
    () => (data ? getDispensaryFromData(data) : invalidUrlDispensaryFactory?.(slug)),
    [data, invalidUrlDispensaryFactory, slug],
  )

  useEffect(() => {
    if (loading) {
      return
    }
    setDispensary(dispensary)
  }, [dispensary, loading])

  if (!called || loading) {
    return loadingPageElement
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error.message
    return <ErrorPage showHomeLink>{errorMessage}</ErrorPage>
  }

  if (!dispensary) {
    return (
      <>
        {loadingPageElement}
        {disableInvalidDispensaries && <InvalidDispensaryModal isOpen />}
      </>
    )
  }

  return (
    <DispensaryContext.Provider value={dispensary}>
      <DispensaryAuth0Provider>{children}</DispensaryAuth0Provider>
    </DispensaryContext.Provider>
  )
}
